<script setup>
import { useUserStore } from "@/store/user-store";
import { Column, DataTable, Dialog, Rating } from "primevue";
import { onMounted, ref, watch, watchEffect } from "vue";
import AnLoader from "../common/AnLoader.vue";
import AnButton from "../common/AnButton.vue";
import {
  chartColors,
  gridHighlightPlugin,
  lineChartOptions,
} from "@/services/constant-services";
import Chart from "primevue/chart";
import { convertToInr } from "@/services/common-services";

const props = defineProps({
  isMFDialogOpen: {
    type: Boolean,
    default: false,
  },
  fund: {
    type: Object,
    default: {},
  },
});

const emit = defineEmits(["update:isMFDialogOpen"]);

const dialogVisible = ref(props.isMFDialogOpen);
const dataLoading = ref(false);
const fundData = ref(null);
const historyTableData = ref(null);
const years = ref(null);
const sipReturnData = ref(null);
const generalData = ref(null);
const rollingReturnData = ref(null);
const riskData = ref(null);
const trailingData = ref(null);
const store = useUserStore();

const loadMutualFundData = async () => {
  dataLoading.value = true;
  try {
    const response = await store.getMutualFundData(props.fund.Id);
    console.log("Mutual Fund Data:", response);
    fundData.value = response;
  } catch (error) {
    store.fireAlert("Error", error.message);
  } finally {
    dataLoading.value = false;
  }
};

watchEffect(() => {
  if (!fundData.value) return;

  years.value = [
    ...new Set([
      ...Object.keys(fundData.value.aumHistory),
      ...Object.keys(fundData.value.navHistory),
      ...Object.keys(fundData.value.mfAnnualReturns),
      ...Object.keys(fundData.value.benchmarkReturns),
    ]),
  ].sort();

  historyTableData.value = [
    {
      metric: "NAV (₹)",
      //Add tooltip
      tooltip: "",
      ...Object.fromEntries(
        years.value.map((year) => [
          year,
          fundData.value.navHistory[year] ?? null,
        ])
      ),
    },
    {
      metric: "Net Assets (₹ Crore)",
      //Add tooltip
      tooltip: "",
      ...Object.fromEntries(
        years.value.map((year) => [
          year,
          fundData.value.aumHistory[year] ?? null,
        ])
      ),
    },

    {
      metric: "Total Returns (%)",
      //Add tooltip
      tooltip: "",
      ...Object.fromEntries(
        years.value.map((year) => [
          year,
          fundData.value.mfAnnualReturns[year] ?? null,
        ])
      ),
    },
    {
      metric: "Index (%)",
      //Add tooltip
      tooltip: "",
      ...Object.fromEntries(
        years.value.map((year) => [
          year,
          fundData.value.benchmarkReturns[year] ?? null,
        ])
      ),
    },
  ];

  sipReturnData.value = {
    labels: fundData.value.sipReturnsData.map((item) =>
      new Date(item.date).toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      })
    ),
    datasets: [
      {
        data: fundData.value.sipReturnsData.map(
          (item) => item.wealthAccumulated
        ),
        label: "Comulative Wealth",
        borderColor: chartColors[0],
        tension: 0,
        fill: false,
      },
      {
        data: fundData.value.sipReturnsData.map((item) => item.investmentVal),
        label: "Invested Amount",
        borderColor: chartColors[1],
        tension: 0,
        fill: false,
      },
    ],
  };

  rollingReturnData.value = {
    labels: fundData.value.rollingReturnsData.map((item) =>
      new Date(item.date).toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      })
    ),
    datasets: [
      {
        data: fundData.value.rollingReturnsData.map(
          (item) => item.rollingRetMF
        ),
        label: "Mutual Fund",
        borderColor: chartColors[0],
        tension: 0,
        fill: false,
      },
      {
        data: fundData.value.rollingReturnsData.map(
          (item) => item.rollingRetBenchmark
        ),
        label: "Benchmark",
        borderColor: chartColors[1],
        tension: 0,
        fill: false,
      },
    ],
  };

  generalData.value = [
    {
      key: "Launch Date",
      value: fundData.value.launchDate,
    },
    {
      key: "Category",
      value: fundData.value.fundCategory,
    },
    {
      key: "Rating",
      value: "",
    },
    {
      key: "Expense Ratio",
      value: fundData.value.expenseRatio,
      //Add tooltip
      tooltip: "",
    },
    {
      key: "Benchmark",
      value: fundData.value.benchmarkName,
    },
    {
      key: "Benchmark TRI 1 Year",
      value: fundData.value.benchmarkTRI1Yr,
      //Add tooltip
      tooltip: "",
    },
  ];

  riskData.value = [
    {
      key: "Standard Deviation (%)",
      value: fundData.value.standardDev,
      //Add tooltip
      tooltip: "",
    },
    {
      key: "Sharpe Ratio",
      value: fundData.value.sharpeRatio,
      //Add tooltip
      tooltip: "",
    },
    {
      key: "Beta",
      value: fundData.value.beta,
      //Add tooltip
      tooltip: "",
    },
    {
      key: "Jensen Alpha (%)",
      value: fundData.value.alpha,
      //Add tooltip
      tooltip: "",
    },
    {
      key: "Portfolio Turnover (%)",
      value: fundData.value.portfolioTurnover,
      //Add tooltip
      tooltip: "",
    },
  ];

  trailingData.value = fundData.value.mutualFundTrailingReturns.map(
    (item, index) => {
      const duration = Object.keys(item)[0];
      return {
        duration,
        fund: item[duration],
        benchmark: fundData.value.benchmarkTrailingReturns[index][duration],
      };
    }
  );
});

watch(
  () => props.isMFDialogOpen,
  (newVal) => {
    dialogVisible.value = newVal;
  }
);

watch(dialogVisible, (newVal) => {
  emit("update:isMFDialogOpen", newVal);
});

onMounted(() => {
  loadMutualFundData();
});
</script>

<template>
  <Dialog
    modal
    v-model:visible="dialogVisible"
    :closable="false"
    :dismissable-mask="!dataLoading"
  >
    <template #header>
      <div
        class="d-flex align-items-center position-relative w-100 p-20 dialog-header"
      >
        <span class="text-center text-primary w-100 font-20 font-weight-700">{{
          fund.fundName
        }}</span>
        <i
          @click="emit('update:isMFDialogOpen', false)"
          class="pi pi-times bg-error text-white p-5 br-5 position-absolute r-0 mr-10 cursor-pointer"
        />
      </div>
    </template>
    <div v-if="dataLoading" class="position-relative loader-container">
      <AnLoader text="Mutual Fund data loading..." />
    </div>
    <div v-if="fundData" class="ph-30 pb-30">
      <div class="d-grid input-grid gap-15 font-20 mt-30">
        <div class="d-flex gap-10" v-for="item in generalData">
          <span class="font-weight-600">{{ item.key }}:</span>

          <Rating
            v-if="item.key === 'Rating'"
            :default-value="fundData.fundRating"
            :readonly="true"
            :pt="{
              onIcon: {
                style: {
                  width: '20px',
                  color: 'var(--an-rating)',
                },
              },
              offIcon: {
                style: {
                  width: '20px',
                  color: 'var(--an-gray)',
                },
              },
              root: {
                class: 'gap-10 w-max',
              },
            }"
          />
          <span v-else class="font-weight-400">
            {{ item.value }}
            <i
              v-if="item.tooltip && item.key === 'Expense Ratio'"
              v-tooltip.right="{
                value: item.tooltip,
                class: 'left-arrow short-width',
              }"
              class="pi pi-info-circle font-15"
            />
            <i
              v-else-if="item.tooltip && item.key === 'Benchmark TRI 1 Year'"
              v-tooltip.left="{
                value: item.tooltip,
                class: 'right-arrow short-width',
              }"
              class="pi pi-info-circle font-15"
            />
          </span>
        </div>
      </div>
      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">History</span>
        <span class="h-divider mt-5" />
        <DataTable
          :value="historyTableData"
          class="mt-25"
          :pt="{
            headerRow: { class: 'font-weight-500' },
          }"
        >
          <Column class="font-weight-600 text-primary">
            <template #body="props">
              <div class="d-flex align-items-center justify-center gap-10">
                {{ props.data.metric }}
                <i
                  v-if="props.data.tooltip"
                  v-tooltip.right="{
                    value: props.data.tooltip,
                    class: 'left-arrow short-width',
                  }"
                  class="pi pi-info-circle font-15"
                /></div
            ></template>
          </Column>
          <Column v-for="year in years" :key="year">
            <template #header="props">
              <div class="text-center w-100">{{ props.column.key }}</div>
            </template>
            <template #body="props">
              <div class="text-center">{{ props.data[year] || "-" }}</div>
            </template></Column
          >
        </DataTable>
      </div>
      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600"
          >Key Considerations for Investors</span
        >
        <span class="h-divider mt-5" />
        <span class="mt-20">{{ fundData.keyConsiderationPara }}</span>
        <span class="font-weight-600 mt-15 font-20">Capital Gains Tax:</span>
        <span>{{ fundData.capitalGainsTax }}</span>
        <span class="font-weight-600 mt-15 font-20">Dividend Taxation:</span>
        <span>{{ fundData.dividendsTaxation }}</span>
      </div>
      <div class="d-flex mt-30 gap-20">
        <template v-if="fundData.fundCategory && fundData.fundCategory.includes('Equity')">
          <!-- Equity Mutual Fund Layout -->
          <div class="flex-1 d-flex flex-direction-column">
            <span class="font-20 font-weight-600">Top Holdings</span>
            <span class="h-divider mt-5" />
            <DataTable
              :value="fundData.stockHoldings"
              class="flex-1 mt-20"
              :pt="{ headerRow: { class: 'font-weight-500' } }"
            >
              <Column header="Stock Name" field="stockName" />
              <Column header="Sector" field="stockSector" />
              <Column
                header="Holdings (%)"
                field="stockPercent"
                class="text-center"
                :pt="{
                  columnHeaderContent: {
                    class: 'justify-center',
                  },
                }"
              />
            </DataTable>
          </div>
          <div class="flex-1 d-flex flex-direction-column">
            <span class="font-20 font-weight-600">Sector Weights</span>
            <span class="h-divider mt-5" />
            <DataTable
              :value="fundData.sectorHoldings"
              class="flex-1 mt-20"
              :pt="{ headerRow: { class: 'font-weight-500' } }"
            >
              <Column header="Sector" field="sectorName" />
              <Column
                header="Weights (%)"
                field="sectorWeight"
                class="text-center"
                :pt="{
                  columnHeaderContent: {
                    class: 'justify-center',
                  },
                }"
              />
            </DataTable>
          </div>
        </template>
        <template v-else-if="fundData.fundCategory && fundData.fundCategory.includes('Debt')">
          <!-- Debt Mutual Fund Layout -->
          <div class="flex-1 d-flex flex-direction-column">
            <div class="debt-summary-wrapper">
              <div class="debt-table-scroll">
                <table class="debt-summary-table mt-15 mb-30">
                  <thead>
                    <tr>
                      <th colspan="4" class="debt-summary-title">Debt Fund Allocation Summary</th>
                    </tr>
                    <tr>
                      <th>Debt Allocation (%)</th>
                      <th>Cash & Cash Equivalent Allocation (%)</th>
                      <th>Credit Quality</th>
                      <th>Interest Rate Sensitivity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="font-weight-600">{{ fundData.debtAllocation }}%</td>
                      <td class="font-weight-600">{{ fundData.cashAndCashEquivalentAllocation }}%</td>
                      <td class="font-weight-600">{{ fundData.creditQuality }}</td>
                      <td class="font-weight-600">{{ fundData.interestRateSensitivity }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="debt-holdings-row-outer mt-30">
              <div class="debt-holdings-row">
                <div class="debt-table-scroll">
                  <table class="debt-holdings-table">
                    <thead>
                      <tr><th colspan="2" class="debt-holdings-title">Top Holdings</th></tr>
                      <tr>
                        <th>Holding Name</th>
                        <th>Holding %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in fundData.topHoldings" :key="item.holdingName">
                        <td>{{ item.holdingName }}</td>
                        <td class="text-center">{{ item.holdingPercent }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="debt-table-scroll">
                  <table class="debt-holdings-table">
                    <thead>
                      <tr><th colspan="2" class="debt-holdings-title">Credit Rating Holdings</th></tr>
                      <tr>
                        <th>Holding Name</th>
                        <th>Holding %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in fundData.creditRatingHoldings" :key="item.holdingName">
                        <td>{{ item.holdingName }}</td>
                        <td class="text-center">{{ item.holdingPercent }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else-if="fundData.fundCategory && fundData.fundCategory.includes('Hybrid')">
          <!-- Hybrid Mutual Fund Layout -->
          <div class="flex-1 d-flex flex-direction-column">
            <div class="hybrid-summary-wrapper">
              <div class="hybrid-table-scroll">
                <table class="hybrid-summary-table mt-15 mb-30">
                  <thead>
                    <tr>
                      <th colspan="3" class="hybrid-summary-title">Hybrid Fund Allocation Summary</th>
                    </tr>
                    <tr>
                      <th>Equity Allocation (%)</th>
                      <th>Debt Allocation (%)</th>
                      <th>Other Asset Class - {{ fundData.otherAssetClass }} Allocation (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="font-weight-600">{{ fundData.equityAllocation }}%</td>
                      <td class="font-weight-600">{{ fundData.debtAllocation }}%</td>
                      <td class="font-weight-600">{{ fundData.otherAssetAllocation }}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="hybrid-holdings-section mt-30">
              <div class="hybrid-holdings-row">
                <div class="hybrid-table-scroll">
                  <table class="hybrid-holdings-table">
                    <thead>
                      <tr><th colspan="2" class="hybrid-holdings-title">Top Equity Holdings in Fund</th></tr>
                      <tr>
                        <th>Holding Name</th>
                        <th>Holding %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in fundData.equityHoldings" :key="item.holdingName">
                        <td>{{ item.holdingName }}</td>
                        <td class="text-center">{{ item.holdingPercent }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="hybrid-table-scroll">
                  <table class="hybrid-holdings-table">
                    <thead>
                      <tr><th colspan="2" class="hybrid-holdings-title">Top Equity Sectors in Fund</th></tr>
                      <tr>
                        <th>Sector Name</th>
                        <th>Sector Weight %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in fundData.equitySectors" :key="item.sectorName">
                        <td>{{ item.sectorName }}</td>
                        <td class="text-center">{{ item.sectorWeight }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="hybrid-holdings-row mt-25">
                <div class="hybrid-table-scroll">
                  <table class="hybrid-holdings-table">
                    <thead>
                      <tr><th colspan="2" class="hybrid-holdings-title">Top Debt Holdings in Fund</th></tr>
                      <tr>
                        <th>Holding Name</th>
                        <th>Holding %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in fundData.debtHoldings" :key="item.holdingName">
                        <td>{{ item.holdingName }}</td>
                        <td class="text-center">{{ item.holdingPercent }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="hybrid-table-scroll">
                  <table class="hybrid-holdings-table">
                    <thead>
                      <tr><th colspan="2" class="hybrid-holdings-title">Top Credit Rating Holdings in Fund</th></tr>
                      <tr>
                        <th>Holding Name</th>
                        <th>Holding %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in fundData.debtRatings" :key="item.holdingName">
                        <td>{{ item.holdingName }}</td>
                        <td class="text-center">{{ item.holdingPercent }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">Fund Returns</span>
        <span class="h-divider mt-5" />
        <span class="text-error mt-25 pl-5"
          >3 years ago, if you had invested {{ convertToInr(100000) }} as
          lumpsum, and {{ convertToInr(10000) }} SIP thereafter...</span
        >
        <div class="d-flex p-20 flex-direction-column input-shadow br-10 mt-20">
          <span class="w-100 text-center font-20 font-weight-600 text-primary"
            >SIP Returns: 3 Years</span
          >
          <div class="d-flex mt-10 gap-20">
            <Chart
              type="line"
              :data="sipReturnData"
              :options="lineChartOptions(false, true)"
              :plugins="[gridHighlightPlugin]"
              class="flex-1"
              :style="{ height: '350px' }"
            />
            <div
              class="d-flex flex-direction-column legend p-10 h-max align-self-center"
            >
              <div class="d-flex gap-10 align-items-center">
                <span
                  :style="{ backgroundColor: chartColors[0] }"
                  class="pt-5 pl-30 h-max"
                />
                <span class="font-weight-500 w-max"
                  >Your Comulative Wealth</span
                >
              </div>
              <div class="d-flex gap-10 align-items-center">
                <span
                  :style="{ backgroundColor: chartColors[1] }"
                  class="pt-5 pl-30 h-max"
                />
                <span class="font-weight-500">Invested Amount</span>
              </div>
            </div>
          </div>
        </div>
        <span class="text-error mt-20 pl-5"
          >1 Year Rolling Returns, rolled monthly, for last 4 years</span
        >
        <div class="d-flex p-20 flex-direction-column input-shadow br-10 mt-20">
          <div class="d-flex mt-10 gap-20">
            <Chart
              type="line"
              :data="rollingReturnData"
              :options="lineChartOptions(false, true, true)"
              :plugins="[gridHighlightPlugin]"
              class="flex-1"
              :style="{ height: '350px' }"
            />
            <div
              class="d-flex flex-direction-column legend p-10 h-max align-self-center"
            >
              <div class="d-flex gap-10 align-items-center">
                <span
                  :style="{ backgroundColor: chartColors[0] }"
                  class="pt-5 pl-30 h-max"
                />
                <span class="font-weight-500 w-max">Mutual Fund</span>
              </div>
              <div class="d-flex gap-10 align-items-center">
                <span
                  :style="{ backgroundColor: chartColors[1] }"
                  class="pt-5 pl-30 h-max"
                />
                <span class="font-weight-500">Benchmark</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex mt-30 gap-20">
        <div class="flex-1 d-flex flex-direction-column">
          <span class="font-20 font-weight-600">Trailing Returns</span>
          <span class="h-divider mt-5" />
          <DataTable
            :value="trailingData"
            class="mt-20"
            :pt="{
              headerRow: { class: 'font-weight-500' },
            }"
          >
            <Column class="text-primary" header="Duration" field="duration" />
            <Column header="Fund (%)" field="fund" />
            <Column header="Benchmark (%)" field="benchmark" />
          </DataTable>
        </div>
        <div class="flex-1 d-flex flex-direction-column">
          <span class="font-20 font-weight-600">Risk Parameters</span>
          <span class="h-divider mt-5" />
          <DataTable
            :value="riskData"
            class="mt-20"
            :pt="{
              headerRow: { class: 'font-weight-500' },
            }"
          >
            <Column class="text-primary" header="Parameters">
              <template #body="props">
                <div class="d-flex align-items-center gap-10">
                  {{ props.data.key }}
                  <i
                    v-if="props.data.tooltip"
                    v-tooltip.top="{
                      value: props.data.tooltip,
                      class: 'bottom-arrow short-width',
                    }"
                    class="pi pi-info-circle font-15"
                  /></div
              ></template>
            </Column>
            <Column header="Fund" field="value" />
          </DataTable>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-center w-100 p-10">
        <AnButton
          label="Back"
          variant="primary"
          :is-disabled="dataLoading"
          @click="emit('update:isMFDialogOpen', false)"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.loader-container {
  height: 400px;
}

.dialog-header {
  border-bottom: 2px solid;
  border-bottom-color: var(--an-primary);
}

.input-grid {
  grid-template-columns: repeat(3, 1fr);
}

.legend {
  border: 1px solid var(--an-gray1);
}

.debt-summary-wrapper {
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}
.debt-table-scroll {
  overflow-x: auto;
  max-width: 100%;
}
.debt-summary-table {
  width: 960px;
  max-width: 960px;
  border-collapse: collapse;
  margin-bottom: 0;
  min-width: 600px;
}
.debt-summary-title {
  background: #f8fafc;
  font-weight: 700;
  font-size: 18px;
  text-align: center;
  padding: 12px 8px;
}
.debt-summary-table th, .debt-summary-table td {
  border: 1px solid #e2e8f0;
  padding: 12px 8px;
  text-align: center;
  font-size: 15px;
}
.debt-summary-table th {
  background: #f8fafc;
  font-weight: 700;
}
.debt-holdings-row-outer {
  width: 100%;
  display: block;
  margin-top: 30px;
}
.debt-holdings-row {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 30px;
  margin: 0 auto;
  max-width: 960px;
  flex-wrap: nowrap;
}
.debt-holdings-table {
  flex: 1;
  width: 465px;
  max-width: 465px;
  min-width: 300px;
  border-collapse: collapse;
  background: #fff;
}
.debt-holdings-title {
  background: #f8fafc;
  font-weight: 700;
  font-size: 16px;
  text-align: center;
  padding: 10px 8px;
}
.debt-holdings-table th, .debt-holdings-table td {
  border: 1px solid #e2e8f0;
  padding: 10px 8px;
  text-align: left;
  font-size: 14px;
}
.debt-holdings-table th {
  background: #f8fafc;
  font-weight: 700;
  text-align: left;
}
.debt-holdings-table th:last-child, .debt-holdings-table td:last-child {
  text-align: center;
}

/* Hybrid Fund Styles */
.hybrid-summary-wrapper {
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}
.hybrid-table-scroll {
  overflow-x: auto;
  max-width: 100%;
}
.hybrid-summary-table {
  width: 720px;
  max-width: 720px;
  border-collapse: collapse;
  margin-bottom: 0;
  min-width: 500px;
}
.hybrid-summary-title {
  background: #f8fafc;
  font-weight: 700;
  font-size: 18px;
  text-align: center;
  padding: 12px 8px;
}
.hybrid-summary-table th, .hybrid-summary-table td {
  border: 1px solid #e2e8f0;
  padding: 12px 8px;
  text-align: center;
  font-size: 15px;
}
.hybrid-summary-table th {
  background: #f8fafc;
  font-weight: 700;
}
.hybrid-holdings-section {
  width: 100%;
  display: block;
}
.hybrid-holdings-row {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 30px;
  margin: 0 auto;
  max-width: 960px;
  flex-wrap: nowrap;
}
.hybrid-holdings-table {
  flex: 1;
  width: 465px;
  max-width: 465px;
  min-width: 300px;
  border-collapse: collapse;
  background: #fff;
}
.hybrid-holdings-title {
  background: #f8fafc;
  font-weight: 700;
  font-size: 16px;
  text-align: center;
  padding: 10px 8px;
}
.hybrid-holdings-table th, .hybrid-holdings-table td {
  border: 1px solid #e2e8f0;
  padding: 10px 8px;
  text-align: left;
  font-size: 14px;
}
.hybrid-holdings-table th {
  background: #f8fafc;
  font-weight: 700;
  text-align: left;
}
.hybrid-holdings-table th:last-child, .hybrid-holdings-table td:last-child {
  text-align: center;
}

@media (max-width: 1000px) {
  .debt-summary-table {
    width: 100%;
    max-width: 100%;
    min-width: 600px;
  }
  .debt-holdings-row {
    max-width: 100%;
  }
  .debt-holdings-table {
    width: calc(50% - 15px);
    max-width: none;
    min-width: 280px;
  }
  .hybrid-summary-table {
    width: 100%;
    max-width: 100%;
    min-width: 500px;
  }
  .hybrid-holdings-row {
    max-width: 100%;
  }
  .hybrid-holdings-table {
    width: calc(50% - 15px);
    max-width: none;
    min-width: 280px;
  }
}
@media (max-width: 768px) {
  .debt-holdings-row, .hybrid-holdings-row {
    flex-direction: column;
    align-items: center;
    gap: 20px;
    flex-wrap: nowrap;
  }
  .debt-holdings-table, .hybrid-holdings-table {
    width: 100%;
    max-width: 100%;
    min-width: 250px;
  }
  .debt-summary-table, .hybrid-summary-table {
    width: 100%;
    min-width: 400px;
  }
  .debt-summary-title, .hybrid-summary-title {
    font-size: 16px;
    padding: 10px 6px;
  }
  .debt-summary-table th, .debt-summary-table td,
  .hybrid-summary-table th, .hybrid-summary-table td {
    padding: 8px 6px;
    font-size: 13px;
  }
}
@media (max-width: 480px) {
  .debt-summary-table, .hybrid-summary-table {
    min-width: 350px;
  }
  .debt-summary-title, .hybrid-summary-title {
    font-size: 14px;
    padding: 8px 4px;
  }
  .debt-summary-table th, .debt-summary-table td,
  .hybrid-summary-table th, .hybrid-summary-table td {
    padding: 6px 4px;
    font-size: 12px;
  }
  .debt-holdings-title, .hybrid-holdings-title {
    font-size: 14px;
    padding: 8px 6px;
  }
  .debt-holdings-table th, .debt-holdings-table td,
  .hybrid-holdings-table th, .hybrid-holdings-table td {
    padding: 8px 6px;
    font-size: 12px;
  }
}
</style>
